import type React from 'react';
import type { DeckGLRef } from '@deck.gl/react';
import type { MapRef } from 'react-map-gl/maplibre';
import type { ExportLegendGroup } from './atoms';
import { captureChartHighRes } from './chartExportUtils';

// ──── US Letter landscape at 300 DPI ────
export const PAGE_WIDTH = 3300; // 11" * 300
export const PAGE_HEIGHT = 2550; // 8.5" * 300
export const PAGE_WIDTH_IN = 11;
export const PAGE_HEIGHT_IN = 8.5;

// ──── Layout constants (300 DPI pixels; 1pt = 4.17px) ────
const HEADER_HEIGHT = 150; // 0.5"
const HEADER_PAD = 18;
const LOGO_MAX_H = HEADER_HEIGHT - HEADER_PAD * 2;

export const SWATCH_SIZE = 30; // ~7pt
const LEGEND_COL_COUNT = 2;
const LEGEND_ROW_H = 42;
const LEGEND_PAD = 24;
const LEGEND_MAX_WIDTH = 1500; // 5"
const LEGEND_MARGIN = 30;
const GROUP_TITLE_H = 44;
const META_LINE_H = 36;
const INFO_LINE_H = 36;
const GROUP_SPACING = 16;
const META_GAP = 10;
const ITEM_OFFSET_Y = 8;

const NORTH_ARROW_SIZE = 100;
const STATS_PAGE_PAD = 45;

const LEGEND_BG = 'rgba(255, 255, 255, 0.92)';
const LEGEND_BORDER = '#C0C8CC';
const SEPARATOR_COLOR = '#D0D8DD';
const MAP_BG = '#1B1B35';

// Font specs at 300 DPI
const FONT_HEADER_TITLE = 'bold 75px Arial, sans-serif'; // ~18pt
const FONT_LEGEND_TITLE = 'bold 37px Arial, sans-serif'; // ~9pt
const FONT_LEGEND_ITEM = '33px Arial, sans-serif'; // ~8pt
const FONT_LEGEND_META = '30px Arial, sans-serif'; // ~7pt

export function sanitizeFilename(name: string): string {
  return (
    name
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1f]/g, '')
      .replace(/[<>:"/\\|?*]/g, '_')
      .replace(/\.\./g, '_')
      .slice(0, 200)
  );
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

export async function captureMapCanvas(
  deckRef: React.RefObject<DeckGLRef | null>,
  mapRef: React.RefObject<MapRef | null>
): Promise<HTMLCanvasElement> {
  const deckGLRef = deckRef.current;
  const map = mapRef.current;
  if (!deckGLRef?.deck || !map) {
    throw new Error('Map or DeckGL ref is not available');
  }

  const deckInstance = deckGLRef.deck;
  deckInstance.redraw('export');

  const mapInstance = map.getMap();
  mapInstance.triggerRepaint();
  let idleHandler: (() => void) | null = null;
  const idleResult = await Promise.race([
    new Promise<'idle'>(resolve => {
      idleHandler = () => resolve('idle');
      mapInstance.once('idle', idleHandler);
    }),
    new Promise<'timeout'>(resolve => setTimeout(() => resolve('timeout'), 3000)),
  ]);
  if (idleResult === 'timeout') {
    if (idleHandler) mapInstance.off('idle', idleHandler);
    console.warn('Map did not reach idle state within 3s; export may contain incomplete tiles.');
  }

  const mapCanvas = mapInstance.getCanvas();
  const deckCanvas = deckInstance.getCanvas();
  if (!mapCanvas || !deckCanvas) {
    throw new Error('Could not access map or deck canvas');
  }

  const width = mapCanvas.width;
  const height = mapCanvas.height;

  const composite = document.createElement('canvas');
  composite.width = width;
  composite.height = height;
  const ctx = composite.getContext('2d');
  if (!ctx) {
    throw new Error('Could not create canvas context');
  }

  ctx.drawImage(mapCanvas, 0, 0);
  ctx.drawImage(deckCanvas, 0, 0, deckCanvas.width, deckCanvas.height, 0, 0, width, height);

  return composite;
}

function drawNorthArrow(ctx: CanvasRenderingContext2D, cx: number, cy: number, bearing: number) {
  const r = NORTH_ARROW_SIZE / 2;
  const baseHW = Math.round(r * 0.36);
  const nFontPx = Math.round(r * 0.7);
  const nGap = Math.round(r * 0.12);
  const angle = -bearing * (Math.PI / 180);

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(angle);

  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.lineTo(baseHW, r * 0.4);
  ctx.lineTo(0, r * 0.15);
  ctx.closePath();
  ctx.fillStyle = '#333333';
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(0, -r);
  ctx.lineTo(-baseHW, r * 0.4);
  ctx.lineTo(0, r * 0.15);
  ctx.closePath();
  ctx.fillStyle = '#999999';
  ctx.fill();

  ctx.fillStyle = '#333333';
  ctx.font = `bold ${nFontPx}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText('N', 0, -r - nGap);

  ctx.restore();
}

function measureLegendBox(
  ctx: CanvasRenderingContext2D,
  legendGroups: ExportLegendGroup[]
): { width: number; height: number } {
  if (legendGroups.length === 0) return { width: 0, height: 0 };

  let contentH = 0;
  let maxColWidth = 0;

  for (let gi = 0; gi < legendGroups.length; gi++) {
    const group = legendGroups[gi];
    contentH += GROUP_TITLE_H;
    const rows = Math.ceil(group.items.length / LEGEND_COL_COUNT);
    contentH += rows * LEGEND_ROW_H;

    if (group.model) contentH += META_LINE_H;
    if (group.updatedOn) contentH += META_LINE_H;
    if (group.model || group.updatedOn) contentH += META_GAP;

    if (gi < legendGroups.length - 1) contentH += GROUP_SPACING;

    ctx.font = FONT_LEGEND_ITEM;
    for (const item of group.items) {
      const textW = ctx.measureText(item.label).width;
      maxColWidth = Math.max(maxColWidth, SWATCH_SIZE + 10 + textW);
    }
  }

  contentH += INFO_LINE_H * 2;

  const colWidth = Math.min(maxColWidth + 10, LEGEND_MAX_WIDTH / LEGEND_COL_COUNT);
  const boxWidth = Math.min(LEGEND_PAD * 2 + colWidth * LEGEND_COL_COUNT, LEGEND_MAX_WIDTH);
  const boxHeight = LEGEND_PAD + contentH + LEGEND_PAD;

  return { width: boxWidth, height: boxHeight };
}

function drawLegendBox(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  legendGroups: ExportLegendGroup[],
  bearing: number
) {
  const { width: boxW, height: boxH } = measureLegendBox(ctx, legendGroups);
  if (boxW === 0) return;

  const boxTop = y - boxH;

  ctx.fillStyle = LEGEND_BG;
  ctx.fillRect(x, boxTop, boxW, boxH);
  ctx.strokeStyle = LEGEND_BORDER;
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, boxTop + 0.5, boxW - 1, boxH - 1);

  let cursorY = boxTop + LEGEND_PAD;
  const colWidth = (boxW - LEGEND_PAD * 2) / LEGEND_COL_COUNT;

  for (let gi = 0; gi < legendGroups.length; gi++) {
    const group = legendGroups[gi];

    ctx.fillStyle = '#444444';
    ctx.font = FONT_LEGEND_TITLE;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(group.title, x + LEGEND_PAD, cursorY);
    cursorY += GROUP_TITLE_H;

    group.items.forEach((item, i) => {
      const col = i % LEGEND_COL_COUNT;
      const row = Math.floor(i / LEGEND_COL_COUNT);
      const ix = x + LEGEND_PAD + col * colWidth;
      const iy = cursorY + row * LEGEND_ROW_H;

      ctx.fillStyle = item.color;
      ctx.fillRect(ix, iy + ITEM_OFFSET_Y, SWATCH_SIZE, SWATCH_SIZE);

      ctx.fillStyle = '#444444';
      ctx.font = FONT_LEGEND_ITEM;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(
        item.label,
        ix + SWATCH_SIZE + 10,
        iy + ITEM_OFFSET_Y,
        colWidth - SWATCH_SIZE - 14
      );
    });

    const rows = Math.ceil(group.items.length / LEGEND_COL_COUNT);
    cursorY += rows * LEGEND_ROW_H;

    if (group.model || group.updatedOn) {
      cursorY += META_GAP;
      ctx.fillStyle = '#777777';
      ctx.font = FONT_LEGEND_META;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      if (group.model) {
        ctx.fillText(`Model: ${group.model}`, x + LEGEND_PAD, cursorY, boxW - LEGEND_PAD * 2);
        cursorY += META_LINE_H;
      }
      if (group.updatedOn) {
        ctx.fillText(`Updated: ${group.updatedOn}`, x + LEGEND_PAD, cursorY, boxW - LEGEND_PAD * 2);
        cursorY += META_LINE_H;
      }
    }

    if (gi < legendGroups.length - 1) cursorY += GROUP_SPACING;
  }

  const dateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  ctx.fillStyle = '#777777';
  ctx.font = FONT_LEGEND_META;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(dateStr, x + LEGEND_PAD, cursorY);
  ctx.fillText('Source: FEMA / NLT', x + LEGEND_PAD, cursorY + INFO_LINE_H);

  const arrowCx = x + boxW - LEGEND_PAD - NORTH_ARROW_SIZE / 2;
  const arrowCy = cursorY + (INFO_LINE_H * 2) / 2;
  drawNorthArrow(ctx, arrowCx, arrowCy, bearing);
}

function drawHeader(
  ctx: CanvasRenderingContext2D,
  width: number,
  title: string,
  tempoLogo: HTMLImageElement,
  impactLogo: HTMLImageElement
): void {
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, HEADER_HEIGHT);

  const tempoAspect = tempoLogo.naturalWidth / tempoLogo.naturalHeight;
  const tempoH = LOGO_MAX_H;
  const tempoW = tempoH * tempoAspect;
  ctx.drawImage(tempoLogo, HEADER_PAD, HEADER_PAD, tempoW, tempoH);

  const impactAspect = impactLogo.naturalWidth / impactLogo.naturalHeight;
  const impactH = LOGO_MAX_H;
  const impactW = impactH * impactAspect;
  ctx.drawImage(impactLogo, width - HEADER_PAD - impactW, HEADER_PAD, impactW, impactH);

  ctx.fillStyle = '#222222';
  ctx.font = FONT_HEADER_TITLE;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const maxTitleW = width - tempoW - impactW - HEADER_PAD * 4;
  let displayTitle = title;
  if (ctx.measureText(displayTitle).width > maxTitleW) {
    while (ctx.measureText(displayTitle + '...').width > maxTitleW && displayTitle.length > 0) {
      displayTitle = displayTitle.slice(0, -1);
    }
    displayTitle += '...';
  }
  ctx.fillText(displayTitle, width / 2, HEADER_HEIGHT / 2);

  ctx.fillStyle = SEPARATOR_COLOR;
  ctx.fillRect(0, HEADER_HEIGHT - 1, width, 1);
}

export async function renderBrandedExport(
  mapCanvas: HTMLCanvasElement,
  title: string,
  legendGroups: ExportLegendGroup[],
  bearing: number,
  tempoLogo: HTMLImageElement,
  impactLogo: HTMLImageElement
): Promise<HTMLCanvasElement> {
  const output = document.createElement('canvas');
  output.width = PAGE_WIDTH;
  output.height = PAGE_HEIGHT;
  const ctx = output.getContext('2d');
  if (!ctx) {
    throw new Error('Could not create output canvas context');
  }

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, PAGE_WIDTH, PAGE_HEIGHT);

  drawHeader(ctx, PAGE_WIDTH, title, tempoLogo, impactLogo);

  // Map area below header - fill with dark background for letterboxing
  const mapAreaW = PAGE_WIDTH;
  const mapAreaH = PAGE_HEIGHT - HEADER_HEIGHT;
  ctx.fillStyle = MAP_BG;
  ctx.fillRect(0, HEADER_HEIGHT, mapAreaW, mapAreaH);

  // Scale map to fit while preserving aspect ratio
  const mapAspect = mapCanvas.width / mapCanvas.height;
  const areaAspect = mapAreaW / mapAreaH;

  let drawW: number, drawH: number, drawX: number, drawY: number;
  if (mapAspect >= areaAspect) {
    drawW = mapAreaW;
    drawH = mapAreaW / mapAspect;
    drawX = 0;
    drawY = HEADER_HEIGHT + (mapAreaH - drawH) / 2;
  } else {
    drawH = mapAreaH;
    drawW = mapAreaH * mapAspect;
    drawX = (mapAreaW - drawW) / 2;
    drawY = HEADER_HEIGHT;
  }
  ctx.drawImage(mapCanvas, drawX, drawY, drawW, drawH);

  // Floating legend box (bottom-right corner of map area)
  const legendBoxBottom = PAGE_HEIGHT - LEGEND_MARGIN;
  if (legendGroups.length > 0) {
    const { width: boxW } = measureLegendBox(ctx, legendGroups);
    drawLegendBox(ctx, PAGE_WIDTH - LEGEND_MARGIN - boxW, legendBoxBottom, legendGroups, bearing);
  } else {
    // Standalone north arrow when no legend
    const r = NORTH_ARROW_SIZE / 2;
    const nFontPx = Math.round(r * 0.7);
    const nGap = Math.round(r * 0.12);
    const aboveCenter = r + nGap + nFontPx;
    const belowCenter = Math.round(r * 0.4);
    const arrowPad = 20;
    const arrowBgW = NORTH_ARROW_SIZE + arrowPad * 2;
    const arrowBgH = aboveCenter + belowCenter + arrowPad * 2;
    const arrowX = PAGE_WIDTH - LEGEND_MARGIN - arrowBgW;
    const arrowY = PAGE_HEIGHT - LEGEND_MARGIN - arrowBgH;
    ctx.fillStyle = LEGEND_BG;
    ctx.fillRect(arrowX, arrowY, arrowBgW, arrowBgH);
    ctx.strokeStyle = LEGEND_BORDER;
    ctx.lineWidth = 1;
    ctx.strokeRect(arrowX + 0.5, arrowY + 0.5, arrowBgW - 1, arrowBgH - 1);
    drawNorthArrow(ctx, arrowX + arrowBgW / 2, arrowY + arrowPad + aboveCenter, bearing);
  }

  return output;
}

async function captureElementToCanvas(
  element: HTMLDivElement | null,
  scale = 2
): Promise<HTMLCanvasElement | null> {
  if (!element) return null;
  const { default: html2canvas } = await import('html2canvas-pro');
  return html2canvas(element, {
    scale,
    scrollX: 0,
    scrollY: -window.scrollY,
    useCORS: true,
    backgroundColor: 'white',
    ignoreElements: el => el.getAttribute('data-print') === 'hidden',
  });
}

export async function captureStatsPage(
  pageWidth: number,
  pageHeight: number,
  title: string,
  tempoLogo: HTMLImageElement,
  impactLogo: HTMLImageElement,
  statsContext: 'home' | 'incident',
  chartCanvases: Record<string, HTMLCanvasElement | null>,
  statsContainerRef: HTMLDivElement | null
): Promise<HTMLCanvasElement> {
  const output = document.createElement('canvas');
  output.width = pageWidth;
  output.height = pageHeight;
  const ctx = output.getContext('2d');
  if (!ctx) {
    throw new Error('Could not create stats page canvas context');
  }

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, pageWidth, pageHeight);

  drawHeader(ctx, pageWidth, title, tempoLogo, impactLogo);

  let cursorY = HEADER_HEIGHT + STATS_PAGE_PAD;
  const contentWidth = pageWidth - STATS_PAGE_PAD * 2;

  if (statsContext === 'incident') {
    const statsCanvas = await captureElementToCanvas(statsContainerRef);
    if (statsCanvas) {
      const scale = Math.min(
        contentWidth / statsCanvas.width,
        (pageHeight * 0.3) / statsCanvas.height
      );
      const scaledW = statsCanvas.width * scale;
      const scaledH = statsCanvas.height * scale;
      const statsX = STATS_PAGE_PAD + (contentWidth - scaledW) / 2;
      ctx.drawImage(statsCanvas, statsX, cursorY, scaledW, scaledH);
      cursorY += scaledH + STATS_PAGE_PAD;
    }

    const chartAreaWidth = (contentWidth - STATS_PAGE_PAD) / 2;
    const chartAreaHeight = pageHeight - cursorY - STATS_PAGE_PAD;

    const countyCanvas = await captureChartHighRes(
      chartCanvases['countyBar'],
      chartAreaWidth,
      chartAreaHeight
    );
    const timeSeriesCanvas = await captureChartHighRes(
      chartCanvases['timeSeries'],
      chartAreaWidth,
      chartAreaHeight
    );

    if (countyCanvas) {
      const scale = Math.min(
        chartAreaWidth / countyCanvas.width,
        chartAreaHeight / countyCanvas.height
      );
      const w = countyCanvas.width * scale;
      const h = countyCanvas.height * scale;
      ctx.drawImage(countyCanvas, STATS_PAGE_PAD, cursorY, w, h);
    }

    if (timeSeriesCanvas) {
      const scale = Math.min(
        chartAreaWidth / timeSeriesCanvas.width,
        chartAreaHeight / timeSeriesCanvas.height
      );
      const w = timeSeriesCanvas.width * scale;
      const h = timeSeriesCanvas.height * scale;
      ctx.drawImage(
        timeSeriesCanvas,
        STATS_PAGE_PAD + chartAreaWidth + STATS_PAGE_PAD,
        cursorY,
        w,
        h
      );
    }
  } else {
    const scatterAvailH = pageHeight - cursorY - STATS_PAGE_PAD;
    const scatterTargetH = Math.min(scatterAvailH, Math.round(pageHeight * 0.55));
    const scatterCanvas = await captureChartHighRes(
      chartCanvases['scatter'],
      contentWidth,
      scatterTargetH
    );

    if (scatterCanvas) {
      const scale = Math.min(
        contentWidth / scatterCanvas.width,
        scatterTargetH / scatterCanvas.height
      );
      const w = scatterCanvas.width * scale;
      const h = scatterCanvas.height * scale;
      const chartX = STATS_PAGE_PAD + (contentWidth - w) / 2;
      const chartY = cursorY + (scatterAvailH - h) / 2;
      ctx.drawImage(scatterCanvas, chartX, chartY, w, h);
    }
  }

  return output;
}

/**
 * Wait for a paint cycle to complete after a state change.
 * Uses two nested requestAnimationFrame calls to guarantee
 * the browser has completed at least one full paint cycle.
 */
export function waitForPaintCycle(): Promise<void> {
  return new Promise<void>(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}
